// 다색 날씨 아이콘 — 가독성 위해 구름은 흰색(연한 외곽선)·해는 노랑/주황.
// Material Symbols(단색) 대체용. 날씨 카드 메인 아이콘·주간예보 공통 사용.
// 색상은 날씨 시각용 리터럴(센서 톤과 동일한 하드코딩 예외).
import React from 'react';

const C = {
  sun: '#FFA000', // 해 본체 (주황)
  sunHi: '#FFD54F', // 해 하이라이트 (노랑)
  ray: '#FFB300', // 햇살
  cloud: '#FFFFFF', // 구름 (흰색)
  cloudEdge: '#C7D6E2', // 구름 외곽선 (연한 회청색 — 밝은 배경에서도 형태 인지)
  rain: '#4FA3F7', // 빗방울
  snow: '#9CC9F0', // 눈송이
  bolt: '#FFC107', // 번개
} as const;

// MUI cloud 패스 (viewBox 0 0 24 24, x:0~24 / y:4~20)
const CLOUD = 'M19.35 10.04A7.49 7.49 0 0 0 12 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 0 0 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z';

/** 8방향 햇살 + 본체 + 하이라이트. cloud 뒤에 깔면 겹친 살은 구름이 가린다. */
function Sun({ cx, cy, r }: { cx: number; cy: number; r: number }): React.ReactElement {
  const dirs: ReadonlyArray<[number, number]> = [
    [0, -1], [1, 0], [0, 1], [-1, 0], [0.7, -0.7], [0.7, 0.7], [-0.7, 0.7], [-0.7, -0.7],
  ];
  const inner = r + 1.6;
  const outer = r + 3.2;
  return (
    <g>
      {dirs.map(([dx, dy], i) => (
        <line
          key={i}
          x1={cx + dx * inner}
          y1={cy + dy * inner}
          x2={cx + dx * outer}
          y2={cy + dy * outer}
          stroke={C.ray}
          strokeWidth={1.8}
          strokeLinecap="round"
        />
      ))}
      <circle cx={cx} cy={cy} r={r} fill={C.sun} />
      <circle cx={cx - r * 0.28} cy={cy - r * 0.28} r={r * 0.52} fill={C.sunHi} />
    </g>
  );
}

function Cloud({ transform }: { transform?: string }): React.ReactElement {
  return (
    <path
      d={CLOUD}
      fill={C.cloud}
      stroke={C.cloudEdge}
      strokeWidth={0.9}
      strokeLinejoin="round"
      transform={transform}
    />
  );
}

export function WeatherIcon({
  code,
  size = 32,
  style,
}: {
  code: string;
  size?: number;
  style?: React.CSSProperties;
}): React.ReactElement {
  let content: React.ReactNode;
  switch (code) {
    case 'sunny':
      content = <Sun cx={12} cy={12} r={5.5} />;
      break;
    case 'partly_cloudy_day':
      // 해(우상단) → 구름(좌하단) 순서로 그려 겹친 부분은 구름이 가린다.
      content = (
        <>
          <Sun cx={16.5} cy={8} r={3.6} />
          <Cloud transform="translate(-2.5 5) scale(0.78)" />
        </>
      );
      break;
    case 'rainy':
      content = (
        <>
          <Cloud transform="translate(0.6 -2.5) scale(0.92)" />
          <g stroke={C.rain} strokeWidth={1.8} strokeLinecap="round">
            <line x1="7.5" y1="17.5" x2="6" y2="21.5" />
            <line x1="12" y1="17.5" x2="10.5" y2="21.5" />
            <line x1="16.5" y1="17.5" x2="15" y2="21.5" />
          </g>
        </>
      );
      break;
    case 'snowy':
      content = (
        <>
          <Cloud transform="translate(0.6 -2.5) scale(0.92)" />
          <g fill={C.snow}>
            <circle cx="7.2" cy="19.5" r="1.3" />
            <circle cx="11.7" cy="20.8" r="1.3" />
            <circle cx="16.2" cy="19.5" r="1.3" />
          </g>
        </>
      );
      break;
    case 'thunderstorm':
      content = (
        <>
          <Cloud transform="translate(0.6 -2.5) scale(0.92)" />
          <path d="M13 15.5 l-4 5 h2.6 l-1.4 3.5 4.6-5.6 h-2.8 z" fill={C.bolt} />
        </>
      );
      break;
    case 'cloudy':
    default:
      content = <Cloud transform="translate(0.3 1) scale(0.97)" />;
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.12))', ...style }}
    >
      {content}
    </svg>
  );
}
